import { Box, CircularProgress } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useInteractionMetadata, useFileUrl } from "../../../hooks/useInteraction";
import { useTranscript } from "../../../hooks/useTranscription";
import { useGetSummary } from "../../../hooks/useSummary";
import { usePopoutManager } from "../../hooks/usePopoutManager";
import type { PlaybackCommandAction } from "../../types/popout";
import WaveformPlayer from "../Waveform/WaveformPlayer";
import type { PlayerHandle } from "../Waveform/WaveformPlayer";
import DetailHeader from "../DetailPane/DetailHeader";
import DetailTabs, { TabId } from "../DetailPane/DetailTabs";
import TranscriptTab from "../DetailPane/TranscriptTab";
import SummaryTab from "../DetailPane/SummaryTab";
import MetadataTab from "../DetailPane/MetadataTab";
import ClosePromptDialog from "./ClosePromptDialog";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Interaction types that have audio/video media. */
const MEDIA_TYPES = new Set(["Audio", "Video", "AudioAndVideo"]);

function formatDuration(ms?: number): string {
  if (typeof ms !== "number" || !ms) return "";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function formatTimestamp(value?: string): string {
  if (!value || typeof value !== "string") return "";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PopoutPlayerPage() {
  const { interactionId = "", customerId = "" } = useParams<{
    interactionId: string;
    customerId: string;
  }>();
  const [searchParams] = useSearchParams();
  const initialTime = Number.parseFloat(searchParams.get("t") ?? "0") || 0;

  // ------------------------------------------------------------------
  // Popout manager (registers this window as "popout" type)
  // ------------------------------------------------------------------
  const {
    playerClosed,
    broadcastPlayback,
    notifyDetachTab,
    setOnPlaybackCommand,
    unregister,
  } = usePopoutManager({ windowType: "popout", interactionId });

  // ------------------------------------------------------------------
  // Data fetching — mirrors DetailPane patterns
  // ------------------------------------------------------------------
  const { data: metadata, isLoading: metadataLoading } = useInteractionMetadata(
    customerId,
    interactionId,
  );

  const interactionType = (metadata?.interactionType as string) ?? "Audio";
  const hasMedia = MEDIA_TYPES.has(interactionType);

  const { data: fileUrlData, isLoading: mediaLoading } = useFileUrl(
    interactionId,
    customerId,
    hasMedia,
  );

  const { data: transcript, isLoading: transcriptLoading } = useTranscript(
    customerId,
    interactionId,
  );

  const { data: summary, isLoading: summaryLoading } = useGetSummary(
    customerId,
    interactionId,
  );

  // ------------------------------------------------------------------
  // Derived header fields
  // ------------------------------------------------------------------
  const agentName = (metadata?.agentName as string) ?? "";
  const startTime = formatTimestamp(metadata?.startTime as string | undefined);
  const duration = formatDuration(metadata?.durationMs as number | undefined);
  const phoneNumber = (metadata?.phoneNumber as string) ?? "";

  // ------------------------------------------------------------------
  // beforeunload cleanup — fires before React unmount on hard closes
  // ------------------------------------------------------------------
  useEffect(() => {
    const handleBeforeUnload = () => unregister();
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [unregister]);

  // ------------------------------------------------------------------
  // Document title
  // ------------------------------------------------------------------
  useEffect(() => {
    if (agentName) {
      document.title = `${agentName} — Player`;
    }
  }, [agentName]);

  // ------------------------------------------------------------------
  // Player ref & playback broadcast
  // ------------------------------------------------------------------
  const playerRef = useRef<PlayerHandle>(null);

  const handleTimeUpdate = useCallback(
    (timeSeconds: number) => {
      const player = playerRef.current;
      if (!player) return;
      const state = player.getState();
      broadcastPlayback(interactionId, {
        currentTime: timeSeconds,
        duration: state.duration,
        isPlaying: state.isPlaying,
        playbackRate: state.playbackRate,
      });
    },
    [broadcastPlayback, interactionId],
  );

  // ------------------------------------------------------------------
  // Inbound PLAYBACK_COMMAND from detached tabs
  // ------------------------------------------------------------------
  useEffect(() => {
    setOnPlaybackCommand((cmd: PlaybackCommandAction) => {
      const player = playerRef.current;
      if (!player) return;
      switch (cmd.action) {
        case "play":
          player.play();
          break;
        case "pause":
          player.pause();
          break;
        case "seek":
          player.seek(cmd.time);
          break;
        case "setRate":
          player.setRate(cmd.rate);
          break;
      }
    });
  }, [setOnPlaybackCommand]);

  // ------------------------------------------------------------------
  // Tab state — active tab & detached tabs
  // ------------------------------------------------------------------
  const [activeTab, setActiveTab] = useState<TabId>("transcript");
  const [currentTimeSeconds, setCurrentTimeSeconds] = useState(0);

  // Proxy time update to also update local state for transcript highlighting
  const handleTimeUpdateFull = useCallback(
    (t: number) => {
      setCurrentTimeSeconds(t);
      handleTimeUpdate(t);
    },
    [handleTimeUpdate],
  );

  // Seek to initial time (passed via ?t= query param from main window)
  const resumedRef = useRef(false);
  const handlePlayerReady = useCallback(() => {
    if (!resumedRef.current && initialTime > 0 && playerRef.current) {
      playerRef.current.seek(initialTime);
      resumedRef.current = true;
    }
  }, [initialTime]);

  // ------------------------------------------------------------------
  // Tab detach: open new window
  // ------------------------------------------------------------------
  const handleDetachTab = useCallback(
    (tab: TabId) => {
      const url = `/customers/${customerId}/v2/popout/${interactionId}/tab/${tab}`;
      const win = window.open(url, `detached-${interactionId}-${tab}`, "width=600,height=700");
      if (!win) {
        alert("Please allow pop-ups for this site to detach tabs.");
        return;
      }
      notifyDetachTab(interactionId, tab);
    },
    [customerId, interactionId, notifyDetachTab],
  );

  // ------------------------------------------------------------------
  // Close dialog
  // ------------------------------------------------------------------
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);

  const handleCloseButtonClick = useCallback(() => {
    setCloseDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    // Close the window entirely
    unregister();
    window.close();
  }, [unregister]);

  const handleDialogReturn = useCallback(() => {
    // Return to main window — close this pop-out, main window will restore DetailPane
    unregister();
    window.close();
  }, [unregister]);

  // ------------------------------------------------------------------
  // Loading state
  // ------------------------------------------------------------------
  if (metadataLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "var(--bg-surface)",
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        backgroundColor: "var(--bg-surface)",
      }}
    >
      {/* Header — no pop-out button since we are already popped out */}
      <DetailHeader
        agentName={agentName || "\u2014"}
        dateTime={startTime || "\u2014"}
        duration={duration || "\u2014"}
        phoneNumber={phoneNumber || "\u2014"}
        onClose={handleCloseButtonClick}
      />

      {/* Waveform player */}
      {hasMedia && (
        <WaveformPlayer
          ref={playerRef}
          mediaUrl={fileUrlData?.temporaryUrl}
          isLoading={mediaLoading}
          isTranscoding={!!fileUrlData?.transcodingJobId}
          interactionType={interactionType}
          redactionTimestamps={
            metadata?.redactionTimestamps as
              | { start: string; end: string }[]
              | undefined
          }
          autoPlay
          onReady={handlePlayerReady}
          onTimeUpdate={handleTimeUpdateFull}
        />
      )}

      {/* Tab bar */}
      <DetailTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onDetachTab={handleDetachTab}
      />

      {/* Tab content */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        {activeTab === "transcript" && (
          <TranscriptTab
            customerId={customerId}
            interactionId={interactionId}
            interactionType={interactionType}
            hasMedia={hasMedia}
            transcript={transcript ?? undefined}
            isLoading={transcriptLoading}
            currentTimeSeconds={currentTimeSeconds}
          />
        )}
        {activeTab === "summary" && (
          <SummaryTab
            customerId={customerId}
            interactionId={interactionId}
            summary={summary ?? undefined}
            isLoading={summaryLoading}
            hasTranscript={!!transcript}
            hasMedia={hasMedia}
            interactionType={interactionType}
            onSummaryRequested={() => {}}
          />
        )}
        {activeTab === "metadata" && (
          <MetadataTab metadata={metadata ?? {}} />
        )}
      </Box>

      {/* Close confirmation dialog */}
      <ClosePromptDialog
        open={closeDialogOpen}
        parentType="popout"
        playerClosed={playerClosed}
        onReturn={handleDialogReturn}
        onClose={handleDialogClose}
      />
    </Box>
  );
}
